import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useNavigate, useParams } from "react-router-dom";
import CustomFormBuilder from "./CustomFormBuilder";

import PageBanner from "../../components/PageBanner";
import {
  fetchQuestionnaire,
  updateQuestionnaire,
} from "../../services/questionnaireService";
import { AppToast } from "../../components/AppFeedback";
import {
  normalizeQuestionSchemaForBackend,
  ensureSliderHideValueFlagForCreator,
} from "./questionnaireSchemaUtils";
import { useUserContext } from "../../context/UserContext";

const QuestionnaireEdit = () => {
  const navigate = useNavigate();
  const { qid } = useParams();
  const { roles = [] } = useUserContext();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    label: "",
    question_schema: { pages: [{ name: "page1", elements: [] }] },
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [fullViewOpen, setFullViewOpen] = useState(false);
  const canManageQuestionnaires = roles.some((role) => {
    const upper = String(role).toUpperCase();
    return upper === "ADMIN" || upper === "MANAGER";
  });

  useEffect(() => {
    const loadQuestionnaire = async () => {
      setLoading(true);
      try {
        const data = await fetchQuestionnaire(qid);
        setQuestionnaire(data);
        // Backend may use questionnaire_schema, question_schema, or schema; may return as JSON string
        let schema = data.questionnaire_schema ?? data.question_schema ?? data.schema;
        if (typeof schema === "string") {
          try {
            schema = JSON.parse(schema);
          } catch {
            schema = null;
          }
        }
        const hasValidSchema =
          schema &&
          typeof schema === "object" &&
          !Array.isArray(schema) &&
          Array.isArray(schema.pages);
        let surveyJson = hasValidSchema ? schema : { pages: [{ name: "page1", elements: [] }] };
        surveyJson = ensureSliderHideValueFlagForCreator(surveyJson);
        setEditForm({
          label: data.label || "",
          question_schema: surveyJson,
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || "Failed to load questionnaire",
          severity: "error",
        });
        setTimeout(() => navigate("/questionnaire-management"), 2000);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
  }, [qid, navigate]);

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateQuestionnaire = async () => {
    if (!editForm.label.trim()) {
      setSnackbar({
        open: true,
        message: "Questionnaire label is required",
        severity: "warning",
      });
      return;
    }

    setUpdating(true);
    try {
      const payload = {
        label: editForm.label.trim(),
        question_schema: normalizeQuestionSchemaForBackend(editForm.question_schema || {}),
      };

      await updateQuestionnaire(qid, payload);
      setSnackbar({
        open: true,
        message: "Questionnaire updated successfully.",
        severity: "success",
      });
      setTimeout(() => navigate("/questionnaire-management"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update questionnaire",
        severity: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!questionnaire) {
    return (
      <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
        <PageBanner title="Questionnaire Not Found" />
        <Box sx={{ pt: 3, display: "flex", justifyContent: "center", px: 2 }}>
          <Button variant="contained" onClick={() => navigate("/questionnaire-management")}>
            Back to Questionnaires
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
      <PageBanner title={`Edit Questionnaire: ${questionnaire.name}`} />

      <Box sx={{ pt: 3, display: "flex", justifyContent: "center", mb: 4, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            borderRadius: 1,
            p: { xs: 2, md: 3 },
            width: "100%",
            maxWidth: "1400px",
            bgcolor: "#ffffff",
            minHeight: { xs: "50vh", md: "80vh" },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="QID (Read-only)"
                value={questionnaire.qid || ""}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Questionnaire Name (Read-only)"
                value={questionnaire.name || ""}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="label"
                label="Questionnaire Label (Display Name)"
                placeholder="e.g., Subject Intake Form"
                value={editForm.label}
                onChange={handleEditChange}
                disabled={!canManageQuestionnaires || updating}
                helperText="This appears as the form heading"
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              borderBottom: "2px solid #3f7fbf",
              mb: 2,
              pb: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "#666", fontWeight: 600 }}>
              Edit Form Schema
            </Typography>
            {canManageQuestionnaires && !updating && (
              <IconButton
                aria-label="Full screen"
                onClick={() => setFullViewOpen(true)}
                sx={{ p: 0.5 }}
                title="Full screen (open builder in large modal)"
              >
                <FullscreenIcon />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
              p: 2,
            }}
          >
            {canManageQuestionnaires && !updating && !fullViewOpen && (
              <Box sx={{ flex: 1, overflow: "hidden", display: "flex" }}>
                <CustomFormBuilder
                  key={qid}
                  data={
                    editForm.question_schema?.pages
                      ? editForm.question_schema
                      : { pages: [{ name: "page1", elements: [] }] }
                  }
                  onChange={(data) => setEditForm((prev) => ({ ...prev, question_schema: data }))}
                />
              </Box>
            )}
            {canManageQuestionnaires && !updating && fullViewOpen && (
              <Box sx={{ p: 2, textAlign: "center", color: "#666" }}>
                <Typography>Form builder is open in full screen. Close the modal to return here.</Typography>
              </Box>
            )}
            {(!canManageQuestionnaires || updating) && (
              <Box sx={{ p: 2, textAlign: "center", color: "#999" }}>
                <Typography>Only admin or manager can edit the form schema</Typography>
              </Box>
            )}
          </Box>

          {/* Full-screen modal: large dialog with SurveyJS creator only */}
          <Dialog
            open={fullViewOpen}
            onClose={() => setFullViewOpen(false)}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                width: "98vw",
                maxWidth: "98vw",
                height: "98vh",
                maxHeight: "98vh",
                minHeight: "98vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 0,
              },
            }}
          >
            <DialogTitle sx={{ pr: 10 }}>
              Form builder
              <IconButton
                aria-label="Exit full screen"
                onClick={() => setFullViewOpen(false)}
                sx={{ position: "absolute", right: 44, top: 8 }}
                title="Exit full screen"
              >
                <FullscreenExitIcon />
              </IconButton>
              <IconButton
                aria-label="Close"
                onClick={() => setFullViewOpen(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                flex: 1,
                minHeight: 0,
                p: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                }}
              >
                <CustomFormBuilder
                  key={qid}
                  data={
                    editForm.question_schema?.pages
                      ? editForm.question_schema
                      : { pages: [{ name: "page1", elements: [] }] }
                  }
                  onChange={(data) => setEditForm((prev) => ({ ...prev, question_schema: data }))}
                />
              </Box>
            </DialogContent>
          </Dialog>

          <Grid container spacing={2} sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => navigate("/questionnaire-management")}
                disabled={updating}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleUpdateQuestionnaire}
                disabled={!canManageQuestionnaires || updating || !editForm.label.trim()}
              >
                {updating ? "Updating..." : "Update Questionnaire"}
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 1.5, bgcolor: "#f0f4f8", borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: "#555" }}>
              💡 <strong>Note:</strong> Updating the schema will trigger automatic PDF regeneration in the background.
            </Typography>
          </Box>
        </Box>
      </Box>

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default QuestionnaireEdit;
